import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import SunEditor from 'suneditor-react'
import 'suneditor/dist/css/suneditor.min.css'
import appConfig from 'src/configs/appConfig'
import './style.css'

// SunEditor configuration
const editorOptions = {
  buttonList: [
    ['undo', 'redo'],
    ['bold', 'underline', 'italic', 'strike'],
    ['fontColor', 'hiliteColor'],
    ['removeFormat'],
    ['outdent', 'indent'],
    ['align', 'horizontalRule', 'list'],
    ['table', 'link'],
    ['fullScreen', 'showBlocks', 'codeView'],
  ],
  height: '200px',
}

// Helper function to strip HTML tags
const stripHtml = (html) => {
  if (!html) return ''
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

const Sdgs = () => {
  const navigate = useNavigate()
  const [sdgsList, setSdgsList] = useState([])
  const [filteredSdgs, setFilteredSdgs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState('')
  const [formData, setFormData] = useState({
    id: '',
    lang: 'ID',
    title: '',
    sub_title: '',
    description: '',
    code: '',
    image: '',
  })

  // Fetch SDGS data
  const fetchSdgs = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${appConfig.url.api}/sdgs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      setSdgsList(response.data.data || [])
      setFilteredSdgs(response.data.data || [])
    } catch (error) {
      console.error('Error fetching SDGS:', error)
      // Only show alert for actual errors (not empty data)
      if (error.response?.status !== 404) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to fetch SDGS data',
        })
      }
      // Set empty arrays even on error
      setSdgsList([])
      setFilteredSdgs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSdgs()
  }, [])

  // Search functionality
  useEffect(() => {
    const filtered = sdgsList.filter(
      (sdgs) =>
        sdgs.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sdgs.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sdgs.description?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    setFilteredSdgs(filtered)
  }, [searchTerm, sdgsList])

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file size (max 2MB = 2048KB)
      const maxSize = 2048 * 1024 // 2MB in bytes
      if (file.size > maxSize) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'The image must not be greater than 2048 kilobytes (2MB).',
        })
        e.target.value = '' // Reset input
        return
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Please upload a valid image file (JPEG, PNG, GIF, or WebP).',
        })
        e.target.value = '' // Reset input
        return
      }

      setImageFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  // Open modal for add
  const handleAdd = () => {
    setIsEdit(false)
    setFormData({
      id: '',
      lang: 'ID',
      title: '',
      sub_title: '',
      description: '',
      code: '',
      image: '',
    })
    setImageFile(null)
    setImagePreview('')
    setShowModal(true)
  }

  // Open modal for edit
  const handleEdit = (sdgs) => {
    setIsEdit(true)
    setFormData({
      id: sdgs.id,
      lang: sdgs.lang || 'ID',
      title: sdgs.title,
      sub_title: sdgs.sub_title || '',
      description: sdgs.description,
      code: sdgs.code,
      image: sdgs.image || '',
    })
    setImageFile(null)
    setImagePreview(sdgs.image ? `${appConfig.url.assets}/${sdgs.image}` : '')
    setShowModal(true)
  }

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (!formData.title || !formData.description || !formData.code) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please fill all required fields',
      })
      return
    }

    try {
      const submitData = new FormData()
      submitData.append('lang', formData.lang)
      submitData.append('title', formData.title)
      submitData.append('sub_title', formData.sub_title)
      submitData.append('description', formData.description)
      submitData.append('code', formData.code)
      
      if (imageFile) {
        submitData.append('image', imageFile)
      }

      if (isEdit) {
        // Update - using /sdgs-update/{id} endpoint
        await axios.post(`${appConfig.url.api}/sdgs-update/${formData.id}`, submitData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'SDGS updated successfully',
        })
      } else {
        // Create
        await axios.post(`${appConfig.url.api}/sdgs-insert`, submitData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        })
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'SDGS created successfully',
        })
      }
      setShowModal(false)
      fetchSdgs()
    } catch (error) {
      console.error('Error saving SDGS:', error)
      
      // Handle different types of errors
      let errorMessage = 'Failed to save SDGS'
      
      if (error.response) {
        // Backend returned an error response
        const { data, status } = error.response
        
        // Check for validation errors (422)
        if (status === 422 && data.errors) {
          // Format validation errors
          const errorMessages = Object.entries(data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n')
          
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            html: `<div style="text-align: left; white-space: pre-line;">${errorMessages}</div>`,
          })
          return
        }
        
        // Check for single error message
        if (data.message) {
          errorMessage = data.message
        } else if (data.error) {
          errorMessage = data.error
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection.'
      } else {
        // Something else happened
        errorMessage = error.message || 'An unexpected error occurred'
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: errorMessage,
      })
    }
  }

  // Handle delete
  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Delete - using /sdgs-delete/{id} endpoint
          await axios.delete(`${appConfig.url.api}/sdgs-delete/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          })
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'SDGS has been deleted.',
          })
          fetchSdgs()
        } catch (error) {
          console.error('Error deleting SDGS:', error)
          
          // Handle delete errors
          let errorMessage = 'Failed to delete SDGS'
          
          if (error.response?.data?.message) {
            errorMessage = error.response.data.message
          } else if (error.response?.data?.error) {
            errorMessage = error.response.data.error
          } else if (error.request) {
            errorMessage = 'No response from server. Please check your connection.'
          }
          
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: errorMessage,
          })
        }
      }
    })
  }

  return (
    <div className="sdgs-container">
      {/* Header */}
      <div className="sdgs-header">
        <h1>
          <div className="sdgs-header-icon">🌍</div>
          Sustainable Development Goals (SDGS)
        </h1>
      </div>

      {/* Content Card */}
      <div className="sdgs-content-card">
        {/* Toolbar */}
        <div className="sdgs-toolbar">
          <div className="sdgs-search-box">
            <span className="sdgs-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search SDGS by title, code, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="sdgs-btn-add" onClick={handleAdd}>
            <span>➕</span>
            Add New SDGS
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="sdgs-loading">
            <div className="sdgs-spinner"></div>
            <p>Loading SDGS data...</p>
          </div>
        ) : filteredSdgs.length === 0 ? (
          <div className="sdgs-empty-state">
            <div className="sdgs-empty-icon">📋</div>
            <h3>No SDGS Found</h3>
            <p>
              {searchTerm
                ? 'No results match your search. Try different keywords.'
                : 'Start by adding your first SDGS goal.'}
            </p>
          </div>
        ) : (
          <div className="sdgs-table-wrapper">
            <table className="sdgs-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Language</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSdgs.map((sdgs) => (
                  <tr key={sdgs.id}>
                    <td>
                      <div className="sdgs-code-badge">
                        {sdgs.code}
                      </div>
                    </td>
                    <td>
                      {sdgs.image ? (
                        <img 
                          src={`${sdgs.image}`} 
                          alt={sdgs.title}
                          className="sdgs-table-image"
                        />
                      ) : (
                        <div className="sdgs-no-image">No Image</div>
                      )}
                    </td>
                    <td>
                      <strong>{sdgs.title}</strong>
                    </td>
                    <td>
                      <div className="sdgs-description-preview">
                        {(() => {
                          const plainText = stripHtml(sdgs.description)
                          return plainText?.length > 100
                            ? `${plainText.substring(0, 100)}...`
                            : plainText
                        })()}
                      </div>
                    </td>
                    <td>
                      <span className="sdgs-lang-badge">
                        {sdgs.lang === 'ID' ? '🇮🇩 ID' : '🇬🇧 EN'}
                      </span>
                    </td>
                    <td>
                      <div className="sdgs-actions">
                        <button className="sdgs-btn sdgs-btn-view" onClick={() => 
                            navigate(`/admin/manage-sdgs/detail/${sdgs.id}`)
                        }>
                           View
                        </button>
                       
                        <button
                          className="sdgs-btn sdgs-btn-delete"
                          onClick={() => handleDelete(sdgs.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="sdgs-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="sdgs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sdgs-modal-header">
              <h2>{isEdit ? 'Edit SDGS Goal' : 'Add New SDGS Goal'}</h2>
              <button className="sdgs-modal-close" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="sdgs-modal-body">
                <div className="sdgs-form-group">
                  <label>
                    Language <span className="required">*</span>
                  </label>
                  <select
                    name="lang"
                    className="sdgs-form-select"
                    value={formData.lang}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="ID">🇮🇩 Indonesia</option>
                    <option value="EN">🇬🇧 English</option>
                  </select>
                </div>

                <div className="sdgs-form-group">
                  <label>
                    Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    className="sdgs-form-input"
                    placeholder="e.g., SDG-01, SDG-02"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="sdgs-form-group">
                  <label>
                    Title <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    className="sdgs-form-input"
                    placeholder="e.g., No Poverty"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="sdgs-form-group">
                  <label>
                    Sub Title
                  </label>
                  <input
                    type="text"
                    name="sub_title"
                    className="sdgs-form-input"
                    placeholder="e.g., End poverty in all its forms (optional)"
                    value={formData.sub_title}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="sdgs-form-group">
                  <label>
                    Description <span className="required">*</span>
                  </label>
                  <SunEditor
                    setContents={formData.description}
                    onChange={(content) => {
                      setFormData((prev) => ({
                        ...prev,
                        description: content,
                      }))
                    }}
                    setOptions={editorOptions}
                    placeholder="Enter detailed description of the goal..."
                  />
                </div>

                <div className="sdgs-form-group">
                  <label>Goal Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="sdgs-form-input"
                    onChange={handleImageChange}
                  />
                  {imagePreview && (
                    <div className="sdgs-image-preview">
                      <img src={imagePreview} alt="Preview" />
                    </div>
                  )}
                </div>
              </div>

              <div className="sdgs-modal-footer">
                <button type="button" className="sdgs-btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="sdgs-btn-submit">
                  {isEdit ? 'Update SDGS' : 'Create SDGS'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Sdgs